import mainStore from "../store/mainStore";
import { createStopwatch } from "@/helpers/stopwatch.helper.js";
import sessionService from "./session.service";
import uploaderService from "./uploader.service";
import toolsService from "./tools.service";
import AlertHelper from '@/helpers/alerts.helper.js';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import i18n from '@/helpers/i18n.helper.js';

class AudioRecorderService {
    constructor() {
        this._mediaRecorderOptions = {
            audioBitsPerSecond: 64000
        }
        this._initTimer();
        this._spectrumAnalyzer = null;
    }

    _initTimer() {
        mainStore.state.audioRecorder.recorderTimer = createStopwatch();
        mainStore.state.audioRecorder.recorderTimer.reset()
        mainStore.state.audioRecorder.recorderTimer.pause();
    }

    async getAudioDevices() {
        if (!navigator.mediaDevices?.enumerateDevices) {
            throw new Error();
        }
        await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        let audioDevices = await navigator.mediaDevices.enumerateDevices()
        mainStore.commit('setAvailableDevices', audioDevices.filter(item => item.kind === "audioinput"));
    }

    selectAudioDevice(device) {
        if (device === undefined || device == null) {
            return;
        }

        mainStore.commit("setDevice", device);
    }

    async _initStream() {
        if (mainStore.state.audioRecorder.selectedDevice === undefined || mainStore.state.audioRecorder.selectedDevice == null) {
            throw new Error("Cannot init stream without select device");
        }
        
        let stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: mainStore.state.audioRecorder.selectedDevice.deviceId } });
        mainStore.commit("setMediaStream", stream);
    }

    async record(htmlElement, sessionId) {
        await this._initStream();

        mainStore.state.audioRecorder.isRecordingCanceled = false;
        mainStore.state.audioRecorder.model.sessionId = sessionId;
        mainStore.state.audioRecorder.recorder = new MediaRecorder(mainStore.state.audioRecorder.mediaStream, this._mediaRecorderOptions)
        
        mainStore.state.audioRecorder.recorder.ondataavailable = e => {
            mainStore.commit("appendAudioData", e.data);
        }

        mainStore.state.audioRecorder.recorder.onstop = async () => { 
            try {
                mainStore.commit('setBusyStatus', true);
                this._initTimer();
                if (mainStore.state.audioRecorder.isRecordingCanceled === false) {
                    this._initTimer();
                    let blob = new Blob(mainStore.state.audioRecorder.audioChunks, { type: 'audio/x-mpeg-3' });
                    mainStore.commit("setAudioBlob", blob);
                    let file = new File([blob], `${mainStore.state.audioRecorder.model.sessionId}.mp3`);
                    let formData = new FormData();

                    // download file 
                    let fileName = await sessionService.getSessionNameById(mainStore.state.audioRecorder.model.sessionId);
                    let a = document.createElement('a');
                    a.href = window.URL.createObjectURL(mainStore.state.audioRecorder.audioBlobData);
                    a.download = `${fileName}.mp3`
                    a.click();

                    formData.append("StartTimestamp", mainStore.state.audioRecorder.model.startTimestamp);
                    formData.append("Record", file);
                    let response = await sessionService.saveSessionAudioRecord(mainStore.state.audioRecorder.model.sessionId, formData);
                    if (response.status !== 200) {
                        throw await Promise.reject(response);
                    }
                    var successSwal = Swal.mixin(AlertHelper.informationSuccessElement());
                    successSwal.fire(AlertHelper.successAlert(i18n.global.t('com.validation-error.general-success-info'), i18n.global.t('com.global.success')));
                }
            }
            catch {
                var errorSwal = Swal.mixin(AlertHelper.errorToastElement());
                errorSwal.fire(AlertHelper.successAlert(i18n.globa.t('com.file.audio-recorder.error-session-save-file-on-disk'), i18n.global.t('com.global.success')));
            }
            finally {
                mainStore.commit('setBusyStatus', false);
            }
        }

        mainStore.state.audioRecorder.recorder.onstart = async () => {
            try {
                mainStore.commit('setBusyStatus', true);
                mainStore.state.audioRecorder.recorderTimer.reset()
                mainStore.state.audioRecorder.recorderTimer.start()
                mainStore.state.audioRecorder.model.startTimestamp = (await toolsService.getServerTime()).data;
                var t = Swal.mixin(AlertHelper.informationSuccessElement());
                t.fire(AlertHelper.successAlert(i18n.global.t('com.file.audio-recorder.start-recording'), i18n.global.t('com.global.success')));
            }
            finally {
                mainStore.commit('setBusyStatus', false);
            }
           
        }

        mainStore.state.audioRecorder.recorder.onerror = e => {
            console.log("onerror", e);
        }

        const audioCtx = new AudioContext();
        const analyser = audioCtx.createAnalyser();
        audioCtx.createMediaStreamSource(mainStore.state.audioRecorder.mediaStream).connect(analyser);

        const ctx = htmlElement.getContext("2d");
        const data = new Uint8Array(htmlElement.width);
        ctx.strokeStyle = 'rgba(0,77,204,0.5)';

        if (this._spectrumAnalyzer !== null)
            clearInterval(this._spectrumAnalyzer);

        this._spectrumAnalyzer = setInterval(() => {
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, htmlElement.width, htmlElement.height);
            analyser.getByteTimeDomainData(data);
            ctx.lineWidth = 5;
            ctx.beginPath();
            let x = 0;
            for (let d of data) {
                const y = htmlElement.height - (d / 128) * htmlElement.height / 2;
                x ? ctx.lineTo(x++, y) : ctx.moveTo(x++, y);
            }
            ctx.stroke();
        }, 1000 * htmlElement.width / audioCtx.sampleRate);

        mainStore.state.audioRecorder.recorder?.start();
    }

    stop() {
        mainStore.state.audioRecorder.recorder?.stop();
        mainStore.state.audioRecorder.mediaStream
            .getTracks()
            .forEach(track => {
                track.stop()
            })        
    }

    cancel() {
        this.stop();
    }


    async recordWithPreview(htmlElement, elemId) {
        await this._initStream();

        mainStore.state.audioRecorder.isRecordingCanceled = false;
        mainStore.state.audioRecorder.model.sessionId = elemId;

        const chunks = [];

        const recorder = new MediaRecorder(
            mainStore.state.audioRecorder.mediaStream,
            this._mediaRecorderOptions
        );
        mainStore.state.audioRecorder.recorder = recorder;

        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = async () => {
            try {
                mainStore.commit('setBusyStatus', true);
                this._initTimer();

                if (!mainStore.state.audioRecorder.isRecordingCanceled) {
                    const blob = new Blob(chunks, { type: 'audio/x-mpeg-3' });
                    await this._showPreviewAndMaybeSave(blob, elemId);
                }
            } catch (err) {
                const errorSwal = Swal.mixin(AlertHelper.errorToastElement());
                errorSwal.fire(
                    AlertHelper.successAlert(
                        i18n.global.t('com.file.audio-recorder.error-session-save-file-on-disk'),
                        i18n.global.t('com.global.error')
                    )
                );
            } finally {
                mainStore.commit('setBusyStatus', false);
            }
        };

        recorder.onstart = async () => {
            try {
                mainStore.commit('setBusyStatus', true);
                mainStore.state.audioRecorder.recorderTimer.reset();
                mainStore.state.audioRecorder.recorderTimer.start();
                mainStore.state.audioRecorder.model.startTimestamp = (await toolsService.getServerTime()).data;

                const t = Swal.mixin(AlertHelper.informationSuccessElement());
                t.fire(AlertHelper.successAlert(i18n.global.t('com.file.audio-recorder.start-recording'), i18n.global.t('com.global.success')));
            } finally {
                mainStore.commit('setBusyStatus', false);
            }
        };

        recorder.onerror = (e) => {
            console.log('onerror', e);
        };

        const audioCtx = new AudioContext();
        const analyser = audioCtx.createAnalyser();
        audioCtx.createMediaStreamSource(mainStore.state.audioRecorder.mediaStream).connect(analyser);

        const ctx = htmlElement.getContext('2d');
        const data = new Uint8Array(htmlElement.width);
        ctx.strokeStyle = 'rgba(0,77,204,0.5)';

        if (this._spectrumAnalyzer !== null) clearInterval(this._spectrumAnalyzer);

        this._spectrumAnalyzer = setInterval(() => {
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, htmlElement.width, htmlElement.height);
            analyser.getByteTimeDomainData(data);
            ctx.lineWidth = 5;
            ctx.beginPath();
            let x = 0;
            for (let d of data) {
                const y = htmlElement.height - (d / 128) * (htmlElement.height / 2);
                x ? ctx.lineTo(x++, y) : ctx.moveTo(x++, y);
            }
            ctx.stroke();
        }, (1000 * htmlElement.width) / audioCtx.sampleRate);

        recorder.start();
    }

    async _showPreviewAndMaybeSave(blob, elemId) {
        const objectUrl = window.URL.createObjectURL(blob);

        try {
            const stamp = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').replace('Z', '');
            var fileName = i18n.global.t('com.file.audio-recorder.default-audio-file-name') + elemId + '_' + stamp;

            const result = await Swal.fire({
                title: i18n.global.t('com.file.audio-recorder.review-title'),
                html: `
                <div style="display:flex; flex-direction:column; gap:12px; align-items:stretch;">
                    <audio controls style="width:100%">
                        <source src="${objectUrl}" type="audio/mpeg">
                        ${i18n.global.t('com.file.audio-recorder.error-browser-audio-player')}
                    </audio>
                    <small>${i18n.global.t('com.file.audio-recorder.play-and-decide-to-save')}</small>
                </div>
            `,
                showCancelButton: true,
                confirmButtonText: i18n.global.t('com.global.save'),
                cancelButtonText: i18n.global.t('com.global.cancel'),
                confirmButtonColor: '#d80418',
                cancelButtonColor: '#cacccf',
                focusConfirm: false,
                allowOutsideClick: false,
                allowEscapeKey: true
            });

            if (result.isConfirmed) {
                const file = new File([blob], `${fileName}.mp3`, { type: 'audio/mpeg' });
                const fakeEvent = { target: { files: [file] } };

                const response = await uploaderService.UploadAnnouncementAttachements(elemId, fakeEvent);
                if (response === null || response.length === 0) {
                    throw await Promise.reject(response);
                }

                const successSwal = Swal.mixin(AlertHelper.informationSuccessElement());
                successSwal.fire(AlertHelper.successAlert(i18n.global.t('com.validation-error.general-success-info'), i18n.global.t('com.global.success')));
                mainStore.commit('toggleReloadView');
            } else {

                const askDownload = await Swal.fire({
                    title: i18n.global.t('com.file.audio-recorder.rejected-database-save'),
                    text: i18n.global.t('com.file.audio-recorder.save-locally-audio'),
                    showCancelButton: true,
                    confirmButtonText: i18n.global.t('com.global.download'),
                    cancelButtonText: i18n.global.t('com.global.close'),
                    confirmButtonColor: '#d80418',
                    cancelButtonColor: '#cacccf',
                });

                if (askDownload.isConfirmed) {
                    const a = document.createElement('a');
                    a.href = objectUrl;
                    a.download = `${typeof fileName === 'string' ? fileName : elemId}.mp3`;
                    a.click();
                }
            }
        } finally {
            window.URL.revokeObjectURL(objectUrl);
            this._resetState();
        }
    }

    _resetState({ keepDevice = true } = {}) {

        if (this._spectrumAnalyzer) {
            clearInterval(this._spectrumAnalyzer);
            this._spectrumAnalyzer = null;
        }

        const stream = mainStore.state.audioRecorder.mediaStream;
        if (stream) {
            stream.getTracks().forEach(t => t.stop());
        }
        mainStore.commit('setMediaStream', null);

        mainStore.state.audioRecorder.recorder = null;
        mainStore.state.audioRecorder.audioChunks = [];
        mainStore.commit('setAudioBlob', null);

        mainStore.state.audioRecorder.isRecordingCanceled = false;
        mainStore.state.audioRecorder.model.startTimestamp = null;
        mainStore.state.audioRecorder.model.sessionId = -1;

        if (!keepDevice) {
            mainStore.commit('setDevice', null);
        }

        this._initTimer();
        mainStore.commit('setBusyStatus', false);

        mainStore.commit("setAudioElementId", null);
        mainStore.commit('setRecorderOpenStatus', false);
    }


}

export default new AudioRecorderService();